import { useEffect, useState, useCallback } from 'react';
import {
  api,
  type SlashExecutionListItem,
  type SlashExecutionFull,
  type SlashStatus,
} from '../lib/api';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { EmptyState } from '@/components/ui/EmptyState';
import { Skeleton } from '@/components/ui/Skeleton';
import { PageHeader } from '@/components/layout/PageHeader';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/Dialog';
import { RefreshCw, Search, X } from 'lucide-react';

const STATUS_VARIANT: Record<SlashStatus, 'default' | 'info' | 'success' | 'warning' | 'danger' | 'brand'> = {
  pending: 'warning',
  running: 'info',
  completed: 'success',
  failed: 'danger',
  cancelled: 'default',
};

const STATUS_OPTIONS: Array<{ value: '' | SlashStatus; label: string }> = [
  { value: '', label: 'Todos' },
  { value: 'pending', label: 'Pendente' },
  { value: 'running', label: 'Rodando' },
  { value: 'completed', label: 'Concluído' },
  { value: 'failed', label: 'Falhou' },
  { value: 'cancelled', label: 'Cancelado' },
];

const CHANNEL_OPTIONS = ['', 'telegram', 'dashboard', 'cli'];

export function AdminSlashExecutionsPage() {
  const [executions, setExecutions] = useState<SlashExecutionListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'' | SlashStatus>('');
  const [channelFilter, setChannelFilter] = useState('');
  const [searchQ, setSearchQ] = useState('');
  const [drilldown, setDrilldown] = useState<{ execution: SlashExecutionFull; child_task: Record<string, unknown> | null } | null>(null);
  const [drilldownLoading, setDrilldownLoading] = useState(false);
  const [cancelling, setCancelling] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.listSlashExecutions({
        ...(statusFilter ? { status: statusFilter } : {}),
        ...(channelFilter ? { channel: channelFilter } : {}),
        limit: 100,
      });
      setExecutions(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro listando');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, channelFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  async function openDrilldown(id: string) {
    setDrilldownLoading(true);
    try {
      const data = await api.getSlashExecution(id);
      setDrilldown(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro buscando execução');
    } finally {
      setDrilldownLoading(false);
    }
  }

  async function cancelExec(id: string) {
    setCancelling(id);
    try {
      await api.cancelSlashExecution(id);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro cancelando');
    } finally {
      setCancelling(null);
    }
  }

  const filtered = searchQ
    ? executions.filter((e) =>
        e.raw_input.toLowerCase().includes(searchQ.toLowerCase()) ||
        e.handler_ref.toLowerCase().includes(searchQ.toLowerCase()),
      )
    : executions;

  return (
    <div className="p-6 space-y-4">
      <PageHeader
        title="Slash Executions"
        description="Audit + drilldown das execuções de slash commands."
        actions={
          <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
            <RefreshCw className="w-4 h-4 mr-1" /> Recarregar
          </Button>
        }
      />

      <div className="flex gap-2 items-center">
        <Select
          value={statusFilter}
          onChange={(value) => setStatusFilter(value as '' | SlashStatus)}
          options={STATUS_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
        />
        <Select
          value={channelFilter}
          onChange={(value) => setChannelFilter(value)}
          options={CHANNEL_OPTIONS.map((c) => ({ value: c, label: c || 'Todos canais' }))}
        />
        <div className="flex-1 max-w-md">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-raios-muted" />
            <Input className="pl-8" value={searchQ} onChange={(e) => setSearchQ(e.target.value)} placeholder="raw_input ou handler_ref..." />
          </div>
        </div>
      </div>

      {error && <div className="rounded-md border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-400">{error}</div>}

      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-6 space-y-3">{[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
        ) : filtered.length === 0 ? (
          <EmptyState icon={Search} title="Nada por aqui" description="Ajuste filtros ou execute um slash." />
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-raios-border bg-raios-surface-hover">
              <tr>
                <th className="px-3 py-2 text-left">started</th>
                <th className="px-3 py-2 text-left">channel</th>
                <th className="px-3 py-2 text-left">command</th>
                <th className="px-3 py-2 text-left">handler</th>
                <th className="px-3 py-2 text-left">status</th>
                <th className="px-3 py-2 text-right">duration</th>
                <th className="px-3 py-2 w-32"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((e) => (
                <tr key={e.id} className="border-b border-raios-border/40 hover:bg-raios-surface-hover">
                  <td className="px-3 py-2 text-xs">{new Date(e.started_at).toLocaleString('pt-BR')}</td>
                  <td className="px-3 py-2 text-xs">{e.channel}</td>
                  <td className="px-3 py-2 text-xs"><code className="font-mono">{e.raw_input.slice(0, 60)}</code></td>
                  <td className="px-3 py-2 text-xs">
                    <Badge variant="default" className="mr-1">{e.handler_type}</Badge>
                    <code className="font-mono text-raios-muted">{e.handler_ref}</code>
                  </td>
                  <td className="px-3 py-2"><Badge variant={STATUS_VARIANT[e.status]}>{e.status}</Badge></td>
                  <td className="px-3 py-2 text-right text-xs">{e.duration_ms !== null ? `${e.duration_ms}ms` : '—'}</td>
                  <td className="px-3 py-2">
                    <div className="flex gap-1 justify-end">
                      <Button variant="ghost" size="sm" onClick={() => void openDrilldown(e.id)}>Detalhes</Button>
                      {(e.status === 'pending' || e.status === 'running') && (
                        <Button variant="ghost" size="sm" onClick={() => void cancelExec(e.id)} disabled={cancelling === e.id}>
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Dialog open={drilldown !== null || drilldownLoading} onOpenChange={(open) => !open && setDrilldown(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Drilldown</DialogTitle>
          </DialogHeader>
          {drilldownLoading ? (
            <Skeleton className="h-64 w-full" />
          ) : drilldown ? (
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-semibold mb-1">Execution</h3>
                <pre className="text-xs bg-raios-surface-hover p-3 rounded overflow-auto max-h-80">{JSON.stringify(drilldown.execution, null, 2)}</pre>
              </div>
              {drilldown.child_task && (
                <div>
                  <h3 className="text-sm font-semibold mb-1">Child task</h3>
                  <pre className="text-xs bg-raios-surface-hover p-3 rounded overflow-auto max-h-60">{JSON.stringify(drilldown.child_task, null, 2)}</pre>
                </div>
              )}
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDrilldown(null)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
